import axios from 'axios';
import { handle401 } from './apiAuth';

const BASE_URL =
  (typeof window !== 'undefined' && window.electronEnv?.apiUrl) ||
  process.env.REACT_APP_API_URL ||
  'https://api.app.lxop.in';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

// ── Token refresh ─────────────────────────────────────────────────────────────
// Silently call /auth/refresh when the token has fewer than 7 days left.
// Debounced so only one refresh runs at a time regardless of parallel requests.
let _refreshing = false;

// True when the stored token is an admin shadow session. Such a session is
// deliberately short-lived and must never be refreshed — renewing it would
// drop the shadow claims and silently become a normal long-lived login.
export function isShadowToken(token) {
  try {
    if (!token) return false;
    const c = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !!c?.shadow;
  } catch { return false; }
}


function maybeRefresh() {
  if (_refreshing) return;
  const token = localStorage.getItem('lxops_token');
  if (!token) return;
  if (isShadowToken(token)) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const daysLeft = (payload.exp * 1000 - Date.now()) / 86_400_000;
    if (daysLeft < 7) {
      _refreshing = true;
      api.post('/auth/refresh')
        .then(({ data }) => {
          localStorage.setItem('lxops_token', data.token);
          if (data.user) {
            localStorage.setItem('lxops_user', JSON.stringify(data.user));
          }
        })
        .catch(() => {})
        .finally(() => { _refreshing = false; });
    }
  } catch {
    // malformed token — ignore, expiry guard in AuthContext will handle it
  }
}

// ── Request interceptor — attach current token ────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lxops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — trigger background refresh on successful calls ─────
api.interceptors.response.use(
  (res) => {
    maybeRefresh();
    return res;
  },
  (err) => {
    handle401(err);
    return Promise.reject(err);
  }
);

/**
 * Put the signed-in member back on the newsletter.
 *
 * This one call goes to the admin stack rather than the member API: the member
 * API is at 498 of CloudFormation's 500 resources and cannot take another
 * route, while that stack already holds the Ghost admin key. It is the only
 * action that lifts the reading gate in /posts/{id}.
 */
export async function subscribeToNewsletter() {
  const base = (process.env.REACT_APP_ADMIN_API_URL || 'https://adminapi.app.lxop.in')
    .replace(/\/$/, '');
  const token = localStorage.getItem('lxops_token');
  const res = await fetch(`${base}/member/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: '{}',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not update your subscription.');
  return data;
}

/*
 * The newsletter preference, read and written from the profile.
 *
 * Same stack and the same reason as subscribeToNewsletter above. The reply
 * carries `locks_articles`, which is the bit worth showing a member before
 * they switch it off: on the free tier the newsletter is what keeps
 * members-only posts readable, and paid tiers are exempt.
 */
const adminBase = () => (process.env.REACT_APP_ADMIN_API_URL || 'https://adminapi.app.lxop.in')
  .replace(/\/$/, '');

async function newsletterCall(method, body) {
  const token = localStorage.getItem('lxops_token');
  const res = await fetch(`${adminBase()}/member/newsletter`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not read your email settings.');
  return data;
}

export const getNewsletterPreference = () => newsletterCall('GET');
export const setNewsletterPreference = (subscribed) => newsletterCall('POST', { subscribed });

export default api;
